import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CourseVisual } from '@/components/CourseVisual';
import { ModelLetterDownloadButton } from '@/components/ModelLetterDownloadButton';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import api from '@/utils/api';
import { toast } from 'sonner';
import { BookOpen, Award, TrendingUp, Calendar, ArrowLeft, MessageCircle, Send, Loader2, Bot, User, Mail, Clock, AlertTriangle, Bell, Check, Trophy, Star, Flame, Medal, Crown, Target, Zap, Video, ShoppingBag } from 'lucide-react';
import { motion } from 'framer-motion';

const StudentDashboard = () => {
  const navigate = useNavigate();
  const [enrollments, setEnrollments] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [upcomingSessions, setUpcomingSessions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAITutor, setShowAITutor] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [gamificationStats, setGamificationStats] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [showBadges, setShowBadges] = useState(false);
  const [allBadges, setAllBadges] = useState([]);
  const [liveSessions, setLiveSessions] = useState([]);
  const chatEndRef = useRef(null);

  useEffect(() => {
    fetchData();
    fetchUnreadCount();
    fetchNotifications();
    fetchGamificationData();
    fetchLiveSessions();
  }, []);

  const fetchLiveSessions = async () => {
    try {
      const response = await api.get('/live-sessions/upcoming');
      setLiveSessions(response.data);
    } catch (error) {
      console.error('Failed to fetch live sessions:', error);
    }
  };

  const fetchGamificationData = async () => {
    try {
      const [statsRes, leaderboardRes, badgesRes] = await Promise.all([
        api.get('/gamification/stats'),
        api.get('/gamification/leaderboard?limit=10'),
        api.get('/gamification/badges')
      ]);
      setGamificationStats(statsRes.data);
      setLeaderboard(leaderboardRes.data.leaderboard);
      setAllBadges(badgesRes.data);
    } catch (error) {
      console.error('Failed to fetch gamification data:', error);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const response = await api.get('/chat/unread-count');
      setUnreadMessages(response.data.unread_count);
    } catch (error) {
      // Ignore error
    }
  };

  const fetchNotifications = async () => {
    try {
      const [notificationsRes, unreadCountRes] = await Promise.all([
        api.get('/notifications'),
        api.get('/notifications/unread-count')
      ]);
      setNotifications(notificationsRes.data);
      setUnreadNotifications(unreadCountRes.data.unread_count);
    } catch (error) {
      // Ignore error
    }
  };

  const markNotificationRead = async (notificationId) => {
    try {
      await api.put(`/notifications/${notificationId}/read`);
      fetchNotifications();
    } catch (error) {
      // Ignore error
    }
  };

  const markAllNotificationsRead = async () => {
    try {
      await api.put('/notifications/mark-all-read');
      fetchNotifications();
      toast.success('All notifications marked as read');
    } catch (error) {
      toast.error('Failed to mark notifications as read');
    }
  };

  const handleMessageTeacher = async (courseId) => {
    try {
      const response = await api.get(`/chat/course/${courseId}/start`);
      navigate(`/chat/${response.data.conversation_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Unable to start conversation');
    }
  };

  const fetchData = async () => {
    try {
      const [enrollmentsRes, analyticsRes, sessionsRes] = await Promise.all([
        api.get('/enrollments/my'),
        api.get('/analytics/student'),
        api.get('/live-sessions/upcoming'),
      ]);
      
      setEnrollments(enrollmentsRes.data);
      setAnalytics(analyticsRes.data);
      setUpcomingSessions(sessionsRes.data);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setIsLoading(false);
    }
  };

  const scrollToBottom = () => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [chatMessages]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!chatInput.trim() || isSending) return;

    const userMessage = chatInput.trim();
    setChatInput('');
    setChatMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsSending(true);

    try {
      const response = await api.post('/ai-tutor/chat', { text: userMessage });
      setChatMessages(prev => [...prev, { role: 'assistant', content: response.data.response }]);
    } catch (error) {
      toast.error('Failed to get AI response');
      setChatMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, I encountered an error. Please try again.' }]);
    } finally {
      setIsSending(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FAF9F6' }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4" style={{ borderColor: '#163329' }}></div>
          <p style={{ color: '#5C635A' }}>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-6" style={{ backgroundColor: '#FAF9F6' }} data-testid="student-dashboard">
      <div className="max-w-7xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => navigate('/')}
          className="mb-4"
          data-testid="back-to-home-button"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Home
        </Button>
        <div className="mb-8 flex flex-col sm:flex-row justify-between items-start gap-4">
          <div>
            <h1
              className="text-2xl sm:text-4xl font-bold tracking-tight mb-2"
              style={{ fontFamily: 'Spectral, serif', color: '#163329' }}
            >
              My Learning
            </h1>
            <p className="text-sm sm:text-base" style={{ color: '#5C635A' }}>Track your progress and continue learning</p>
          </div>
          <div className="flex flex-wrap gap-2 w-full sm:w-auto">
            <Button
              variant="outline"
              onClick={() => navigate('/student/profile')}
              className="rounded-full text-xs sm:text-sm"
              data-testid="profile-button"
            >
              <User className="h-4 w-4 mr-1.5" />
              <span>Profile</span>
            </Button>
            
            {/* Notifications Bell */}
            <Dialog open={showNotifications} onOpenChange={setShowNotifications}>
              <DialogTrigger asChild>
                <Button
                  className="rounded-full relative text-xs sm:text-sm"
                  variant="outline"
                  data-testid="notifications-button"
                >
                  <Bell className="h-4 w-4 mr-1.5" />
                  <span>Alerts</span>
                  {unreadNotifications > 0 && (
                    <span 
                      className="absolute -top-2 -right-2 h-5 w-5 rounded-full flex items-center justify-center text-xs text-white"
                      style={{ backgroundColor: '#D86B47' }}
                    >
                      {unreadNotifications}
                    </span>
                  )}
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md max-h-[70vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="flex items-center justify-between" style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
                    <span><Bell className="inline h-5 w-5 mr-2" />Notifications</span>
                    {unreadNotifications > 0 && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={markAllNotificationsRead}
                        className="text-sm"
                        style={{ color: '#D86B47' }}
                      >
                        <Check className="h-4 w-4 mr-1" />
                        Mark all read
                      </Button>
                    )}
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-3 pt-4">
                  {notifications.length === 0 ? (
                    <p className="text-center py-8" style={{ color: '#5C635A' }}>No notifications yet</p>
                  ) : (
                    notifications.map((notif) => (
                      <div 
                        key={notif.id}
                        className={`p-3 rounded-lg border cursor-pointer transition-colors ${notif.read ? 'opacity-60' : ''}`}
                        style={{ 
                          backgroundColor: notif.read ? '#FFFFFF' : '#FFF8E1',
                          borderColor: notif.read ? '#E3E1DB' : '#FFA000'
                        }}
                        onClick={() => !notif.read && markNotificationRead(notif.id)}
                      >
                        <div className="flex justify-between items-start">
                          <h4 className="font-medium text-sm" style={{ color: '#163329' }}>{notif.title}</h4>
                          {!notif.read && (
                            <span className="h-2 w-2 rounded-full" style={{ backgroundColor: '#D86B47' }}></span>
                          )}
                        </div>
                        <p className="text-sm mt-1" style={{ color: '#5C635A' }}>{notif.message}</p>
                        <p className="text-xs mt-2" style={{ color: '#888' }}>
                          {new Date(notif.created_at).toLocaleDateString()} at {new Date(notif.created_at).toLocaleTimeString()}
                        </p>
                      </div>
                    ))
                  )}
                </div>
              </DialogContent>
            </Dialog>
            
            <Button
              onClick={() => navigate('/chat')}
              className="rounded-full relative text-xs sm:text-sm"
              
              data-testid="messages-button"
            >
              <Mail className="h-4 w-4 mr-1.5" />
              <span>Messages</span>
              {unreadMessages > 0 && (
                <span 
                  className="absolute -top-2 -right-2 h-5 w-5 rounded-full flex items-center justify-center text-xs text-white"
                  style={{ backgroundColor: '#D86B47' }}
                >
                  {unreadMessages}
                </span>
              )}
            </Button>
            
            <Button
              onClick={() => navigate('/teacher/products')}
              className="rounded-full text-xs sm:text-sm"
              variant="outline"
              data-testid="my-products-button"
            >
              <ShoppingBag className="h-4 w-4 mr-1.5" />
              <span>Products</span>
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        {analytics && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}>
              <Card style={{ borderColor: '#E3E1DB' }} data-testid="enrolled-courses-stat">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium" style={{ color: '#5C635A' }}>Enrolled Courses</CardTitle>
                  <BookOpen className="h-4 w-4" style={{ color: '#163329' }} />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold" style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
                    {analytics.total_courses}
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}>
              <Card style={{ borderColor: '#E3E1DB' }} data-testid="completed-courses-stat">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium" style={{ color: '#5C635A' }}>Completed</CardTitle>
                  <Award className="h-4 w-4" style={{ color: '#D86B47' }} />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold" style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
                    {analytics.completed_courses}
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}>
              <Card style={{ borderColor: '#E3E1DB' }} data-testid="avg-progress-stat">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium" style={{ color: '#5C635A' }}>Avg Progress</CardTitle>
                  <TrendingUp className="h-4 w-4" style={{ color: '#E2A761' }} />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold" style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
                    {Math.round(analytics.avg_progress)}%
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}>
              <Card style={{ borderColor: '#E3E1DB' }} data-testid="quiz-score-stat">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium" style={{ color: '#5C635A' }}>Quiz Average</CardTitle>
                  <Award className="h-4 w-4" style={{ color: '#163329' }} />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold" style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
                    {Math.round(analytics.avg_quiz_score)}%
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        )}

        {/* Gamification Section - Points, Badges, Leaderboard */}
        {gamificationStats && (
          <div className="mb-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Points Card */}
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}>
                <Card className="bg-gradient-to-br from-amber-50 to-orange-50" style={{ borderColor: '#E2A761' }} data-testid="points-card">
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium" style={{ color: '#5C635A' }}>My Points</CardTitle>
                    <Star className="h-5 w-5" style={{ color: '#E2A761' }} />
                  </CardHeader>
                  <CardContent>
                    <div className="text-4xl font-bold mb-2" style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
                      {gamificationStats.total_points}
                    </div>
                    <div className="flex items-center gap-2 text-sm" style={{ color: '#5C635A' }}>
                      <Trophy className="h-4 w-4" style={{ color: '#E2A761' }} />
                      <span>Rank #{gamificationStats.rank}</span>
                      {gamificationStats.current_streak > 0 && (
                        <>
                          <span className="mx-1">•</span>
                          <Flame className="h-4 w-4" style={{ color: '#D86B47' }} />
                          <span>{gamificationStats.current_streak} day streak</span>
                        </>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>

              {/* Badges Card */}
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }}>
                <Card 
                  className="cursor-pointer hover:shadow-md transition-shadow" 
                  style={{ borderColor: '#E3E1DB' }}
                  onClick={() => setShowBadges(true)}
                  data-testid="badges-card"
                >
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium" style={{ color: '#5C635A' }}>Badges Earned</CardTitle>
                    <Medal className="h-5 w-5" style={{ color: '#D86B47' }} />
                  </CardHeader>
                  <CardContent>
                    <div className="text-4xl font-bold mb-2" style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
                      {gamificationStats.badges_count}/{gamificationStats.total_badges}
                    </div>
                    <div className="flex gap-1 flex-wrap">
                      {gamificationStats.badges_earned.slice(0, 4).map((badge, idx) => (
                        <span 
                          key={idx} 
                          className="inline-flex items-center px-2 py-1 rounded-full text-xs"
                          style={{ backgroundColor: '#FFF8E1', color: '#E2A761' }}
                        >
                          {badge.name}
                        </span>
                      ))}
                      {gamificationStats.badges_count > 4 && (
                        <span className="text-xs" style={{ color: '#5C635A' }}>+{gamificationStats.badges_count - 4} more</span>
                      )}
                    </div>
                    {gamificationStats.badges_count === 0 && (
                      <p className="text-sm" style={{ color: '#5C635A' }}>Complete lessons to earn badges!</p>
                    )}
                  </CardContent>
                </Card>
              </motion.div>

              {/* Leaderboard Preview Card */}
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7 }}>
                <Card 
                  className="cursor-pointer hover:shadow-md transition-shadow" 
                  style={{ borderColor: '#E3E1DB' }}
                  onClick={() => setShowLeaderboard(true)}
                  data-testid="leaderboard-card"
                >
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium" style={{ color: '#5C635A' }}>Leaderboard</CardTitle>
                    <Crown className="h-5 w-5" style={{ color: '#163329' }} />
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {leaderboard.slice(0, 3).map((entry, idx) => (
                        <div 
                          key={entry.student_id} 
                          className={`flex items-center justify-between p-2 rounded-lg ${entry.is_current_user ? 'ring-2' : ''}`}
                          style={{ 
                            backgroundColor: idx === 0 ? '#FFF8E1' : '#FAF9F6',
                            borderColor: entry.is_current_user ? '#163329' : 'transparent'
                          }}
                        >
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-sm" style={{ color: idx === 0 ? '#E2A761' : '#5C635A' }}>
                              #{entry.rank}
                            </span>
                            <span className="text-sm truncate max-w-[100px]" style={{ color: '#163329' }}>
                              {entry.name}
                            </span>
                          </div>
                          <span className="font-semibold text-sm" style={{ color: '#163329' }}>
                            {entry.total_points} pts
                          </span>
                        </div>
                      ))}
                    </div>
                    <Button 
                      variant="ghost" 
                      className="w-full mt-2 text-sm"
                      style={{ color: '#163329' }}
                    >
                      View Full Leaderboard
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            </div>
          </div>
        )}

        {/* Badges Modal */}
        <Dialog open={showBadges} onOpenChange={setShowBadges}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
                <Medal className="h-6 w-6" style={{ color: '#D86B47' }} />
                All Badges
              </DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 pt-4">
              {allBadges.map((badge) => (
                <div 
                  key={badge.id}
                  className={`p-4 rounded-xl text-center transition-all ${badge.earned ? 'bg-gradient-to-br from-amber-50 to-orange-50' : 'bg-gray-100 opacity-60'}`}
                  style={{ borderColor: badge.earned ? '#E2A761' : '#E3E1DB', borderWidth: '1px' }}
                >
                  <div className={`w-12 h-12 mx-auto mb-2 rounded-full flex items-center justify-center ${badge.earned ? 'bg-amber-100' : 'bg-gray-200'}`}>
                    {badge.icon === 'trophy' && <Trophy className="h-6 w-6" style={{ color: badge.earned ? '#E2A761' : '#9CA3AF' }} />}
                    {badge.icon === 'star' && <Star className="h-6 w-6" style={{ color: badge.earned ? '#E2A761' : '#9CA3AF' }} />}
                    {badge.icon === 'medal' && <Medal className="h-6 w-6" style={{ color: badge.earned ? '#D86B47' : '#9CA3AF' }} />}
                    {badge.icon === 'crown' && <Crown className="h-6 w-6" style={{ color: badge.earned ? '#E2A761' : '#9CA3AF' }} />}
                    {badge.icon === 'flame' && <Flame className="h-6 w-6" style={{ color: badge.earned ? '#D86B47' : '#9CA3AF' }} />}
                    {badge.icon === 'book-open' && <BookOpen className="h-6 w-6" style={{ color: badge.earned ? '#163329' : '#9CA3AF' }} />}
                    {badge.icon === 'brain' && <Target className="h-6 w-6" style={{ color: badge.earned ? '#163329' : '#9CA3AF' }} />}
                    {badge.icon === 'coins' && <Zap className="h-6 w-6" style={{ color: badge.earned ? '#E2A761' : '#9CA3AF' }} />}
                    {badge.icon === 'check-circle' && <Check className="h-6 w-6" style={{ color: badge.earned ? '#22C55E' : '#9CA3AF' }} />}
                    {badge.icon === 'footprints' && <TrendingUp className="h-6 w-6" style={{ color: badge.earned ? '#163329' : '#9CA3AF' }} />}
                  </div>
                  <h4 className="font-semibold text-sm mb-1" style={{ color: badge.earned ? '#163329' : '#6B7280' }}>
                    {badge.name}
                  </h4>
                  <p className="text-xs" style={{ color: '#5C635A' }}>{badge.description}</p>
                  {badge.earned && (
                    <span className="inline-block mt-2 px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-700">
                      Earned!
                    </span>
                  )}
                </div>
              ))}
            </div>
          </DialogContent>
        </Dialog>

        {/* Leaderboard Modal */}
        <Dialog open={showLeaderboard} onOpenChange={setShowLeaderboard}>
          <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
                <Trophy className="h-6 w-6" style={{ color: '#E2A761' }} />
                Leaderboard - Top Students
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3 pt-4">
              {leaderboard.map((entry, idx) => (
                <div 
                  key={entry.student_id}
                  className={`flex items-center justify-between p-4 rounded-xl ${entry.is_current_user ? 'ring-2 ring-offset-2' : ''}`}
                  style={{ 
                    backgroundColor: idx === 0 ? '#FFF8E1' : idx === 1 ? '#F5F5F5' : idx === 2 ? '#FDF2E9' : '#FAF9F6',
                    borderColor: entry.is_current_user ? '#163329' : 'transparent'
                  }}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${idx === 0 ? 'bg-amber-400 text-white' : idx === 1 ? 'bg-gray-400 text-white' : idx === 2 ? 'bg-amber-600 text-white' : 'bg-gray-200'}`}>
                      {idx < 3 ? (
                        idx === 0 ? '🥇' : idx === 1 ? '🥈' : '🥉'
                      ) : (
                        entry.rank
                      )}
                    </div>
                    <div>
                      <p className="font-semibold" style={{ color: '#163329' }}>
                        {entry.name} {entry.is_current_user && <span className="text-xs">(You)</span>}
                      </p>
                      <div className="flex items-center gap-2 text-xs" style={{ color: '#5C635A' }}>
                        <span>{entry.lessons_completed} lessons</span>
                        <span>•</span>
                        <span>{entry.badges_count} badges</span>
                        {entry.current_streak > 0 && (
                          <>
                            <span>•</span>
                            <Flame className="h-3 w-3" style={{ color: '#D86B47' }} />
                            <span>{entry.current_streak}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-lg" style={{ color: '#163329' }}>{entry.total_points}</p>
                    <p className="text-xs" style={{ color: '#5C635A' }}>points</p>
                  </div>
                </div>
              ))}
            </div>
          </DialogContent>
        </Dialog>

        {/* Live Sessions Section */}
        {liveSessions.length > 0 && (
          <div className="mb-8">
            <h2 className="text-2xl font-semibold mb-4" style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
              <Video className="inline h-6 w-6 mr-2" />
              Upcoming Live Sessions
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {liveSessions.map((session) => (
                <motion.div
                  key={session.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  <Card 
                    className={`p-4 ${session.status === 'live' ? 'ring-2 ring-red-500' : ''}`} 
                    style={{ borderColor: '#E3E1DB' }}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h4 className="font-semibold" style={{ color: '#163329' }}>{session.title}</h4>
                          {session.status === 'live' && (
                            <span className="px-2 py-0.5 rounded-full text-xs bg-red-500 text-white animate-pulse">
                              LIVE NOW
                            </span>
                          )}
                        </div>
                        <p className="text-sm mb-2" style={{ color: '#5C635A' }}>{session.course_title}</p>
                        <div className="flex items-center gap-4 text-xs" style={{ color: '#5C635A' }}>
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {new Date(session.scheduled_at).toLocaleDateString()}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {new Date(session.scheduled_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </div>
                      </div>
                      {session.status === 'live' && (
                        <Button
                          className="rounded-full"
                          style={{ backgroundColor: '#D86B47' }}
                          onClick={() => navigate(`/live-session/${session.id}`)}
                        >
                          <Video className="h-4 w-4 mr-2" />
                          Join Now
                        </Button>
                      )}
                    </div>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        )}

        {/* My Courses */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-semibold" style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
              My Courses
            </h2>
            <Link to="/courses">
              <Button data-testid="browse-more-courses-button" style={{ backgroundColor: '#163329', color: 'white' }}>
                Browse My Courses
              </Button>
            </Link>
          </div>

          {enrollments.length === 0 ? (
            <Card className="p-12 text-center" style={{ borderColor: '#E3E1DB' }}>
              <BookOpen className="h-12 w-12 mx-auto mb-4" style={{ color: '#5C635A' }} />
              <h3 className="text-xl font-semibold mb-2" style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
                No courses yet
              </h3>
              <p className="mb-4" style={{ color: '#5C635A' }}>Start your learning journey by enrolling in a course</p>
              <Link to="/courses">
                <Button
                  
                  className="rounded-full"
                  data-testid="explore-courses-button"
                >
                  Explore Courses
                </Button>
              </Link>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {enrollments.map((item, index) => (
                <motion.div
                  key={item.enrollment.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card
                    className={`h-full border cursor-pointer hover:-translate-y-1 ${item.is_expired ? 'opacity-75' : ''}`}
                    style={{ 
                      borderColor: item.is_expired ? '#DC2626' : '#E3E1DB', 
                      transition: 'transform 0.2s ease',
                      backgroundColor: item.is_expired ? '#FEF2F2' : undefined
                    }}
                    onClick={() => window.location.href = `/courses/${item.course.id}`}
                    data-testid={`enrolled-course-${item.course.id}`}
                  >
                    <CourseVisual course={item.course} className="aspect-video rounded-t-lg" iconClassName="h-10 w-10" />
                    <CardHeader>
                      <div className="flex items-start justify-between gap-2">
                        <CardTitle
                          className="text-lg line-clamp-2"
                          style={{ fontFamily: 'Spectral, serif', color: '#163329' }}
                        >
                          {item.course.title}
                        </CardTitle>
                        {item.is_expired && (
                          <span 
                            className="px-2 py-1 text-xs rounded-full flex-shrink-0"
                            style={{ backgroundColor: '#DC2626', color: 'white' }}
                            data-testid={`expired-badge-${item.course.id}`}
                          >
                            Expired
                          </span>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {/* Expiry Status */}
                        {item.is_expired ? (
                          <div 
                            className="flex items-center gap-2 p-2 rounded-lg text-sm"
                            style={{ backgroundColor: '#FEE2E2', color: '#DC2626' }}
                            data-testid={`expiry-warning-${item.course.id}`}
                          >
                            <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                            <span>Course access expired. Contact admin for extension.</span>
                          </div>
                        ) : item.days_remaining !== null && (
                          <div 
                            className="flex items-center gap-2 text-sm"
                            style={{ color: item.days_remaining <= 7 ? '#D86B47' : '#5C635A' }}
                            data-testid={`days-remaining-${item.course.id}`}
                          >
                            <Clock className="h-4 w-4" />
                            <span>
                              {item.days_remaining <= 0 
                                ? 'Expires today' 
                                : item.days_remaining === 1 
                                  ? '1 day remaining' 
                                  : `${item.days_remaining} days remaining`}
                            </span>
                          </div>
                        )}
                        
                        <div className="flex justify-between text-sm" style={{ color: '#5C635A' }}>
                          <span>Progress</span>
                          <span className="font-medium">{Math.round(item.enrollment.progress || 0)}%</span>
                        </div>
                        <Progress value={item.enrollment.progress || 0} className="h-2" />
                        <Button
                          size="sm"
                          variant="outline"
                          className="w-full rounded-full mt-2"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMessageTeacher(item.course.id);
                          }}
                          data-testid={`message-teacher-${item.course.id}`}
                        >
                          <MessageCircle className="h-4 w-4 mr-2" />
                          Message Teacher
                        </Button>
                        <ModelLetterDownloadButton courseId={item.course.id} />
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          )}
        </div>

        {/* Upcoming Sessions */}
        {upcomingSessions.length > 0 && (
          <div>
            <h2 className="text-2xl font-semibold mb-6" style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
              Upcoming Live Sessions
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {upcomingSessions.slice(0, 4).map((session, index) => (
                <motion.div
                  key={session.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card style={{ borderColor: '#E3E1DB' }} data-testid={`upcoming-session-${session.id}`}>
                    <CardHeader>
                      <CardTitle style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
                        {session.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center gap-2 mb-4" style={{ color: '#5C635A' }}>
                        <Calendar className="h-4 w-4" />
                        <span>{new Date(session.scheduled_at).toLocaleString()}</span>
                      </div>
                      <Button
                        size="sm"
                        className="rounded-full w-full"
                        style={{ backgroundColor: '#D86B47', color: '#FFFFFF' }}
                        onClick={() => window.open(session.meeting_link, '_blank')}
                        data-testid={`join-session-${session.id}`}
                      >
                        Join Session
                      </Button>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentDashboard;